import { useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs as IDayJs } from "dayjs";
import { isEqual, merge, cloneDeep } from "lodash";
import isoWeek from "dayjs/plugin/isoWeek";
import {
    IDateRangeFilter,
    IDisableQuickFilter,
    IFilterOption,
} from "../filterModal.types";
import {
    DATE_FILTER_OPTIONS,
    FILTERS_FUNCTIONAL_DATE_FORMAT,
    FILTERS_TIME_FORMAT,
} from "../filterModal.constants";
import { IDateFilter } from "./DateRangeFilter";
dayjs.extend(isoWeek);

const useDateRangeFilter = <T extends Record<keyof T, IFilterOption[]>>(
    props: IDateRangeFilter<T>
) => {
    const {
        setSelectedFilters,
        selectedFilters,
        selectedFilterType,
        showTimePicker,
        isSingleFilterMode,
        minDate,
        maxDate,
        handleError,
        maxAllowedRange = 396,
        disabledQuickFilters = [],
    } = props;

    const endDate = dayjs().isAfter(dayjs(maxDate)) ? dayjs(maxDate) : dayjs();
    const DEFAULT_FILTER = {
        from: endDate.valueOf(),
        to: endDate.valueOf(),
        rangeType: "custom",
    };
    const focusedRef = useRef("to");
    const filterOptions = Object.values(DATE_FILTER_OPTIONS).filter(
        (el: IFilterOption) =>
            !disabledQuickFilters.includes(el.key as IDisableQuickFilter)
    ) as Array<IFilterOption>;
    const [localSelectedFilter, setLocalSelectedFilter] =
        useState<IDateFilter>(DEFAULT_FILTER);

    const _currentFilter = useMemo(
        () =>
            (selectedFilters[selectedFilterType]?.[0] ||
                DEFAULT_FILTER) as unknown as IDateFilter,
        [selectedFilters, selectedFilterType]
    );

    const _getDateRange = (filterItem: Partial<IFilterOption>): IDateFilter => {
        let data: IDateFilter = {
            rangeType: filterItem.value ?? "custom",
            from: endDate.valueOf(),
            to: endDate.valueOf(),
        };
        switch (filterItem.value) {
            // use enum variables
            case DATE_FILTER_OPTIONS.past6months.value:
                data = {
                    ...data,
                    from: endDate.subtract(6, "month").startOf("day").valueOf(),
                    to: endDate.endOf("day").valueOf(),
                };
                break;
            case DATE_FILTER_OPTIONS.past3months.value:
                data = {
                    ...data,
                    from: endDate.subtract(3, "month").startOf("day").valueOf(),
                    to: endDate.endOf("day").valueOf(),
                };
                break;
            case DATE_FILTER_OPTIONS.month.value:
                data = {
                    ...data,
                    from: endDate.startOf("month").valueOf(),
                    to: endDate.endOf("month").valueOf(),
                };
                break;
            case DATE_FILTER_OPTIONS.week.value:
                data = {
                    ...data,
                    from: endDate.startOf("isoWeek").valueOf(),
                    to: endDate.endOf("isoWeek").valueOf(),
                };
                break;
            case DATE_FILTER_OPTIONS.yesterday.value:
                data = {
                    ...data,
                    from: endDate.subtract(1, "day").startOf("day").valueOf(),
                    to: endDate.subtract(1, "day").endOf("day").valueOf(),
                };
                break;
            case DATE_FILTER_OPTIONS.tomorrow.value:
                data = {
                    ...data,
                    from: endDate.add(1, "day").startOf("day").valueOf(),
                    to: endDate.add(1, "day").endOf("day").valueOf(),
                };
                break;
            case DATE_FILTER_OPTIONS.custom.value:
            case DATE_FILTER_OPTIONS.today.value:
            default:
                data = {
                    ...data,
                    from: endDate.startOf("day").valueOf(),
                    to: endDate.endOf("day").valueOf(),
                };
                break;
        }
        if (dayjs(data.from).isBefore(dayjs(minDate))) {
            data.from = minDate;
        }
        if (dayjs(data.to).isAfter(maxDate)) {
            data.to = maxDate;
        }
        return data;
    };

    useEffect(() => {
        if (!isEqual(localSelectedFilter, _currentFilter)) {
            let initFilter = cloneDeep(
                merge(localSelectedFilter, _currentFilter)
            );

            if (_currentFilter?.rangeType !== "custom") {
                initFilter = cloneDeep(
                    merge(
                        initFilter,
                        _getDateRange({ value: _currentFilter?.rangeType })
                    )
                );
            }

            setLocalSelectedFilter(
                cloneDeep(
                    merge(initFilter, {
                        to: initFilter.to,
                        from: initFilter.from,
                    })
                )
            );

            if (!isEqual(initFilter, _currentFilter)) {
                setSelectedFilters({
                    ...(isSingleFilterMode ? {} : selectedFilters),
                    [selectedFilterType]: [
                        cloneDeep(
                            merge(initFilter, {
                                from: initFilter.from.valueOf(),
                                to: initFilter.to.valueOf(),
                            })
                        ),
                    ],
                });
            }
        }
    }, [_currentFilter]);
    const _combineDateAndTime = (
        obj: Record<string, IDayJs>,
        type: "from" | "to"
    ) => {
        const { date, time } = obj;
        let _date = "";
        let _time = "";

        if (date) {
            _date = dayjs(date).format(FILTERS_FUNCTIONAL_DATE_FORMAT);
            _time = dayjs(
                type === "from"
                    ? dayjs(localSelectedFilter.from)
                    : dayjs(localSelectedFilter.to)
            ).format(FILTERS_TIME_FORMAT);
        } else {
            _time = dayjs(time).format(FILTERS_TIME_FORMAT);
            _date = dayjs(
                type === "from"
                    ? dayjs(localSelectedFilter.from)
                    : dayjs(localSelectedFilter.to)
            ).format(FILTERS_FUNCTIONAL_DATE_FORMAT);
        }

        const timeObj = dayjs(_time, FILTERS_TIME_FORMAT);
        let dateTime = dayjs(_date)
            .hour(timeObj.hour())
            .minute(timeObj.minute())
            .second(timeObj.second())
            .millisecond(0);

        if (
            type === "to" &&
            dateTime.hour() === 23 &&
            dateTime.minute() === 59
        ) {
            dateTime = dayjs(dateTime).endOf("day");
        }
        return {
            [type]: dateTime.valueOf(),
        };
    };

    const _updateFilter = (updatedFilter: IDateFilter) => {
        const diff =
            dayjs(updatedFilter.to).diff(updatedFilter.from, "day") + 1;

        if (diff > maxAllowedRange) {
            handleError({
                iserror: true,
                key: selectedFilterType,
                err: `Maximum allowed range is ${maxAllowedRange} days`,
            });
        } else {
            handleError({ iserror: false, key: selectedFilterType, err: "" });
        }
        const newSelectedFilter: Partial<T> = {
            ...(isSingleFilterMode ? {} : selectedFilters),
            [selectedFilterType]: [
                {
                    ...updatedFilter,
                    from: updatedFilter.from,
                    to: updatedFilter.to,
                },
            ],
        } as unknown as Partial<T>;

        setLocalSelectedFilter(updatedFilter);
        setSelectedFilters(newSelectedFilter);
    };
    const handleChipClick = (filterItem: IFilterOption) => {
        const updatedFilter = _getDateRange(filterItem);
        _updateFilter(updatedFilter);
    };

    const handleStartTimeChange = (time: IDayJs) => {
        const updatedFilter: IDateFilter = {
            ...localSelectedFilter,
            rangeType: "custom",
            ..._combineDateAndTime({ time }, "from"),
        };

        _updateFilter(updatedFilter);
    };

    const handleEndTimeChange = (time: IDayJs) => {
        const updatedFilter: IDateFilter = {
            ...localSelectedFilter,
            rangeType: "custom",
            ..._combineDateAndTime({ time }, "to"),
        };
        _updateFilter(updatedFilter);
    };

    const _toggleInputFocus = () => {
        const start = document.getElementById("startInput");
        const end = document.getElementById("endInput");
        if (focusedRef.current === "from") {
            focusedRef.current = "to";
            end?.blur();
            start?.focus();
        } else {
            focusedRef.current = "from";
            start?.blur();
            end?.focus();
        }
    };

    const _updateDate = (startDate: IDayJs, endDate: IDayJs) => {
        if (disabledQuickFilters.includes("custom")) {
            _updateFilter(localSelectedFilter);
        } else {
            _toggleInputFocus();
            const updatedFilter: IDateFilter = {
                ...localSelectedFilter,
                rangeType: "custom",
                ..._combineDateAndTime(
                    { date: startDate.startOf("day") },
                    "from"
                ),
                ..._combineDateAndTime({ date: endDate.startOf("day") }, "to"),
            };

            _updateFilter(updatedFilter);
        }
    };

    const handleCalendarChange = (
        ranges: [IDayJs | null, IDayJs | null]
    ): void => {
        let [startDate, endDate] = ranges;

        if (!startDate || !endDate) {
            return;
        }
        const isStartDateInvalid =
            startDate.isBefore(dayjs(minDate)) ||
            startDate.isAfter(dayjs(maxDate));
        const isEndDateInvalid =
            endDate.isAfter(dayjs(maxDate)) || endDate.isBefore(dayjs(minDate));
        if (isStartDateInvalid || isEndDateInvalid) {
            return;
        }
        if (startDate.isAfter(endDate)) {
            [startDate, endDate] = [endDate, startDate];
        }
        _updateDate(startDate, endDate);
    };

    return {
        filterOptions,
        localSelectedFilter,
        showTimePicker,
        isCustomDisabled: disabledQuickFilters.includes("custom"),
        maxAllowedRange,
        minDate,
        maxDate,
        handleChipClick,
        handleStartTimeChange,
        handleEndTimeChange,
        handleCalendarChange,
    };
};
export default useDateRangeFilter;
